import imageCompression from 'browser-image-compression';
import { Bot } from 'ignite360-core';
import { PLATFORM_LOGOS } from '@/constants';

export function startDownload(blob: Blob, downloadName = 'en-US') {
  const url = URL.createObjectURL(blob);
  const anchor: HTMLAnchorElement = document.createElement('a');
  anchor.href = url;
  anchor.download = downloadName;
  anchor.style.position = 'fixed';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

export function copyToClipboard(value: string) {
  const textArea: HTMLTextAreaElement = document.createElement('textarea');
  textArea.value = value;
  textArea.style.position = 'fixed';
  document.body.appendChild(textArea);

  const selection = document.getSelection();
  let selectionBackup;
  if (selection && selection.rangeCount > 0) {
    selectionBackup = selection.getRangeAt(0);
  }

  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);

  if (selection && selectionBackup) {
    selection.removeAllRanges();
    selection.addRange(selectionBackup);
  }
}

export const delay = (amountInMs: number) =>
  new Promise((resolve) => setTimeout(resolve, amountInMs));

export async function getCompressedImage(
  imageFile: File,
  options: Parameters<typeof imageCompression>[1] = {
    useWebWorker: true,
    maxSizeMB: 1,
    fileType: imageFile.type,
  },
): Promise<File> {
  const compressedImage = await imageCompression(imageFile, options);
  return new File([compressedImage], imageFile.name, {
    type: imageFile.type,
  });
}

export function isPlatformConnected(bot: Bot, p: string) {
  const platform = p === 'facebook' ? 'facebookMessenger' : p;
  if (platform === 'facebookMessenger' && bot.platformData[platform]) {
    return (
      bot.platformData[platform].enablePosting &&
      !!(bot.platformData[platform].pageId && bot.platformData[platform].pageAccessToken)
    );
  } else if (platform === 'instagram' && bot.platformData[platform]) {
    return (
      bot.platformData[platform].enablePosting &&
      !!(bot.platformData[platform].pageId && bot.platformData[platform].pageAccessToken)
    );
  } else if (platform === 'linkedIn' && bot.platformData[platform]) {
    return (
      bot.platformData[platform].enablePosting &&
      !!(bot.platformData[platform]._id && bot.platformData[platform].type)
    );
  } else if (platform === 'googleBusiness' && bot.platformData[platform]) {
    return (
      bot.platformData[platform].enablePosting &&
      !!(
        bot.platformData[platform].locationId &&
        bot.platformData[platform].accountId &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        bot.platformData[platform].authToken
      ) &&
      !bot.platformData[platform].isExpired
    );
  }
  return false;
}
export const getPlatformLogo = (platform: string): string => {
  return PLATFORM_LOGOS[platform] ? PLATFORM_LOGOS[platform] : PLATFORM_LOGOS['FALLBACK'];
};

export const isMobile = (): boolean => {
  // Check for mobile-specific user agent patterns
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const mobileRegex =
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i;
  const tabletRegex = /android|ipad|playbook|silk/i;

  if (mobileRegex.test(userAgent) || tabletRegex.test(userAgent)) {
    return true;
  }

  // Check for mobile-specific screen characteristics
  const isPortrait = window.innerHeight > window.innerWidth;
  const hasSmallScreen = window.innerWidth < 768;
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Consider a device mobile if it has a touch screen, small width, and typically portrait orientation
  return hasTouchScreen && hasSmallScreen && isPortrait;
};

/** Reads a human-readable message from a failed API call (axios / Nest JSON body). */
export function extractApiErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const err = error as {
    message?: string;
    response?: {
      data?:
        | string
        | {
            message?: string | string[];
            details?: string;
          };
    };
  };

  const data = err.response?.data;
  if (typeof data === 'string' && data.trim()) {
    return data.trim().slice(0, 800);
  }

  if (data && typeof data === 'object') {
    const rawMessage = data.message;
    const joined = Array.isArray(rawMessage)
      ? rawMessage.map(String).join('; ')
      : rawMessage != null
      ? String(rawMessage)
      : '';
    const msg = joined.trim();
    if (
      msg === 'Upstream request failed' &&
      typeof data.details === 'string' &&
      data.details.trim()
    ) {
      try {
        const parsed = JSON.parse(data.details) as {
          error?: { error_user_msg?: string; message?: string };
        };
        const graphMessage = parsed?.error?.error_user_msg || parsed?.error?.message;
        if (graphMessage?.trim()) {
          return graphMessage.trim().slice(0, 800);
        }
      } catch {
        // Fall through to raw details text.
      }
    }
    if (msg && msg !== 'Invalid request' && msg !== 'Upstream request failed') {
      return msg.slice(0, 800);
    }
    if (typeof data.details === 'string' && data.details.trim()) {
      return data.details.trim().slice(0, 800);
    }
    if (msg) {
      return msg.slice(0, 800);
    }
  }

  if (typeof err.message === 'string' && err.message.trim() && err.message !== 'Network Error') {
    return err.message.trim().slice(0, 800);
  }

  return fallback;
}

/** Notification copy when deleting a post on a connected social platform fails. */
export function getDeletePostErrorNotification(
  error: unknown,
  fallback: string,
): { title: string; description: string } {
  const description = extractApiErrorMessage(error, fallback);

  if (/insufficient instagram permissions|instagram_manage_contents/i.test(description)) {
    return {
      title: 'Instagram permission required',
      description,
    };
  }

  if (/2207085|known meta api issue/i.test(description)) {
    return {
      title: 'Instagram delete unavailable',
      description,
    };
  }

  if (/media type is not supported|media type cannot be deleted/i.test(description)) {
    return {
      title: 'Post cannot be deleted here',
      description,
    };
  }

  return {
    title: 'Could not delete post',
    description,
  };
}
